import { VirtuosoGrid } from "react-virtuoso";
import PiModal, { PiModalProps } from "../../../components/PiModal";
import { forwardRef, useCallback } from "react";
import useCharacterPartLibraryPagination from "./CharacterPartLibraryPagination.hook";
import {
  selectPagedCharacterParts,
  selectSelectedCharacterPartDetails,
} from "../../../app/slices/CharacterPartsSlice";
import { useSelector } from "react-redux";
import { CharacterPartGenderedPairDTO } from "../../../dto/CharacterPartGenderedPairDTO";
import CharacterPartLibraryCard from "./CharacterPartLibraryCard";
import useCharacterPartLibrary from "./CharacterPartLibrary.hook";
import CharacterPartLibraryDetailsColumn from "./CharacterPartLibraryDetailsColumn";
import classNames from "classnames";
import PiInputField from "../../../components/PiInputField";
import CharacterPartFilterDropdown from "./CharacterPartFilterDropdown";
import PiButton from "../../../components/PiButton";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshIcon from "@mui/icons-material/Refresh";

export default function CharacterPartLibrary(props: PiModalProps) {
  const { onClose, isOpen } = props;
  const { handleRangeChanged, setFilter, type, setType, filter, handleRefresh, isRefreshing } =
    useCharacterPartLibraryPagination();
  const _ = useCharacterPartLibrary();

  const pagedCharacterParts = useSelector(selectPagedCharacterParts);
  const selectedDetails = useSelector(selectSelectedCharacterPartDetails);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && props.onSubmit) {
        event.preventDefault();
        onSubmit();
      }
    },
    [props.onSubmit, onClose, selectedDetails]
  );

  const onSubmit = () => {
    if (!selectedDetails) return;
    props.onSubmit?.(selectedDetails);
    onClose();
  };

  const searchField = (
    <div className="flex w-full gap-[8px]">
      <PiInputField
        showSearchIcon
        onChange={setFilter}
        label=""
        placeholder="Search"
        autoFocus={true}
        showCrossIcon={true}
        value={filter}
      />
      <div className="w-[250px]">
        <CharacterPartFilterDropdown selected={type} onChange={setType} />
      </div>
      <div className="h-[36px] w-[36px]" title="Refresh">
        <PiButton variant="piBlueOutlined" onClick={handleRefresh} disabled={isRefreshing}>
          <RefreshIcon fontSize="small" />
        </PiButton>
      </div>
    </div>
  );

  const grid = (
    <div className="relative flex-[2] flex flex-col gap-[8px] p-4">
      {searchField}
      {isRefreshing && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <CircularProgress />
        </div>
      )}
      <VirtuosoGrid
        className="pit-creator-scrollbar"
        totalCount={pagedCharacterParts.length}
        rangeChanged={handleRangeChanged}
        itemContent={(index, item: CharacterPartGenderedPairDTO) => (
          <CharacterPartLibraryCard
            key={index}
            characterPart={pagedCharacterParts[index]}
            onSubmit={onSubmit}
          />
        )}
        listClassName={classNames("grid gap-4 p-4", {
          "grid-cols-6": !selectedDetails,
          "grid-cols-4": selectedDetails,
        })}
        style={{ height: "100%", width: "100%" }}
      />
    </div>
  );

  const detailsColumn = (
    <div className="flex-1">
      <CharacterPartLibraryDetailsColumn onSubmit={() => onSubmit()} />
    </div>
  );

  return (
    <PiModal {...props}>
      <div className="flex w-full h-full gap-[8px]" onKeyDown={handleKeyDown}>
        {grid}
        {selectedDetails && detailsColumn}
      </div>
    </PiModal>
  );
}
